import type { ScanFilterEntity } from 'services/scanResolveApi'

import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom';

import { useScrollPosition, useWindowSize, useFillBottom } from 'hooks'
import { useSnackbar } from 'contexts'
import { toDollar, relativeOrDateTime } from 'utils'

import { Box, styled, Divider, Typography, Stack, Collapse, Checkbox, Tooltip, useTheme } from '@mui/material';
import {
  DataGridPro,
  gridPageCountSelector,
  gridPageSelector,
  gridPaginationRowCountSelector,
  gridPageSizeSelector,
  gridVisibleColumnDefinitionsSelector,
  gridDimensionsSelector,
  useGridApiContext,
  useGridSelector,
  useGridApiRef,
  GridLogicOperator,
  GridRowSelectionModel,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridFilterModel,
  GridSortModel,
  GridColumnVisibilityModel,
  GridDensity,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridDetailPanelToggleCell,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-pro';
import { DataGrid as DataGridCommunity } from '@mui/x-data-grid'

import { DownloadIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react'

import { Pagination, Button, Skeleton } from '@repo/ui'
import { ActionLine, Loader, toolbarLabelSx, toolbarStartIconSx } from 'components'
import { RowsPerPageSelector } from './components'
import { formatPageRange, resolvePagination, toGridPage, toPaginationButtonPage } from './pagination'
import { ViewsDropdown } from './components/TableViews'
import { hasMuiXLicense } from 'config/muiLicense'
import type { TableViewState } from 'types'
import { vhMinus } from 'utils/textZoom'
// import { Actions } from 'store/action-log/types';

import { exportRowsToCsv } from 'utils/exportActivity'
import { toCsvRows } from './utils/toCsvRows'

const BaseDataGrid = (hasMuiXLicense ? DataGridPro : DataGridCommunity) as typeof DataGridPro

// VRG-231 — tree-data expand/collapse glyphs, matching the app's lucide iconography
// instead of MUI's own default expand icon. `GridTreeDataGroupingCell` (the grid's
// built-in renderCell for the tree grouping column, see the `groupingColDef` prop
// doc below) hardcodes `<Icon fontSize="inherit" />` when invoking whatever
// component these slots point at — an SVG attribute lucide icons simply ignore, so
// the fixed `size`/`strokeWidth` below are never overridden by it.
const TreeDataExpandIcon = (props: any) => <ChevronRightIcon size={16} strokeWidth={1.5} {...props} />
const TreeDataCollapseIcon = (props: any) => <ChevronDownIcon size={16} strokeWidth={1.5} {...props} />

// NO CEILING on how wide a data table gets. It fills whatever the page gives it.
//
// There WAS one — `TABLE_MAX_WIDTH = 2400` — on the argument that beyond it "a row is long
// enough that tracking a value back across it is genuinely hard". That argument was made
// about readability in the abstract; the people using these tables want the opposite, and
// said so. A table is a tool for comparing many values at once, and a reader who wants a
// narrower one can narrow the window. A reader stuck behind a cap cannot widen past it.
//
// What the cap actually produced, measured on /internal/companies at a 2800 viewport: the
// grid stopped at 2400 inside a 2544 main column, leaving 120px of dead space against the
// right edge for no benefit the reader asked for.
//
// Nothing replaces it. `withProportionalFlex` below is what makes the table USE the width —
// the two were always separate jobs, one filling the space and one bounding it, and only the
// filling one was ever earning its keep.
//
// If a genuinely too-wide table ever becomes a real complaint, the fix is per-table (fewer
// columns, or a `maxWidth` on that page) rather than a global ceiling that every table pays
// for. That is the same reasoning that keeps the form/prose cap off tables entirely: capping
// a data table to the 1100 form column was measured to produce 692px of horizontal overflow
// on RolePage, pushing columns out of view.

// MUI's own default for a column that declares no `width` (`GRID_STRING_COL_DEF.width`), so a
// width-less column still contributes the width it actually renders at to the proportion below.
const DEFAULT_COLUMN_WIDTH = 100

// VRG-227 — Download-exports-only-the-loaded-page fix. `fetchExportPage` (see the prop doc)
// lets Table walk every row matching the current query instead of just the loaded page, in
// batches of this size. `MAX_EXPORT_ROWS` is a real ceiling, not a soft target: a company with
// tens of thousands of rows must not turn one click into an unbounded fetch loop. Both are named
// constants (not buried in the loop below) because the whole point of this fix is that a cap must
// be DISCLOSED, never silent — when it's hit, `exportCsv` tells the user via a toast rather than
// quietly handing back a truncated file with no explanation (the same wrong-subset failure this
// ticket exists to remove, just in a new shape).
const EXPORT_BATCH_SIZE = 500
const MAX_EXPORT_ROWS = 50000

/**
 * Best-effort replica of MUI's own client-side sort, applied over the FULL assembled result set
 * for a server-paginated export (see `exportCsv`) rather than just whatever page happens to be on
 * screen. None of the three lists this shipped for (Products, Certificates, People) define a
 * custom `sortComparator` on any column, so comparing by each column's own `valueGetter`-derived
 * value + `type` reproduces exactly what the grid would show if every matching row were loaded
 * into it at once. A column that DOES bring a custom `sortComparator` is not consulted here — that
 * would need the grid's private comparator plumbing, which isn't part of the public column API —
 * so a table with one would fall back to this generic comparator for export purposes only.
 */
const getExportSortValue = (column: any, row: any): any => {
  if (!column) return undefined
  let value = row?.[column.field]
  if (typeof column.valueGetter === 'function') {
    try {
      value = column.valueGetter(value, row, column, undefined)
    } catch {
      /* fall through to the raw field */
    }
  }
  return value
}

const compareExportValues = (a: any, b: any, type?: string): number => {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (type === 'dateTime' || type === 'date' || a instanceof Date || b instanceof Date) {
    const at = a instanceof Date ? a.getTime() : new Date(a as any).getTime()
    const bt = b instanceof Date ? b.getTime() : new Date(b as any).getTime()
    return at - bt
  }
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

const sortRowsForExport = (rows: any[], model: GridSortModel | undefined, columns: any[]): any[] => {
  if (!model?.length || !rows.length) return rows
  const colByField = new Map(columns.map((c: any) => [c.field, c]))
  const withKeys = rows.map((row) => ({
    row,
    keys: model.map((clause) => getExportSortValue(colByField.get(clause.field), row)),
  }))
  withKeys.sort((a, b) => {
    for (let i = 0; i < model.length; i++) {
      const col = colByField.get(model[i].field)
      const cmp = compareExportValues(a.keys[i], b.keys[i], col?.type)
      if (cmp !== 0) return model[i].sort === 'desc' ? -cmp : cmp
    }
    return 0
  })
  return withKeys.map((w) => w.row)
}

// A column that labels itself is CONTENT and earns width. One that doesn't is a slot — the
// row-actions kebab, the bulk-select checkbox — whose width is an icon measurement, not a content
// measurement, so stretching it would only push the icon off its own centre. Classified by role
// (does it carry a header label?) rather than by pixel width, because the same kebab is authored
// 54, 56 and 64px across this app and all three have to land on the same side of the test.
const isContentColumn = (col: any) =>
  typeof col.headerName === 'string' && col.headerName.trim().length > 0

/**
 * VRG-325 — make a table fill the width it is given, from ONE place rather than 35 pages' column
 * defs (which would drift the moment someone adds a table).
 *
 * Every list authors fixed `width:` px and no `flex`, so the column layout came out
 * BYTE-IDENTICAL at 1280, 1920 and 3440 and the surplus landed in dead `.MuiDataGrid-filler`.
 * Measured on /internal/packages at a 2000 viewport: a 1696px grid in an ~1888px container.
 *
 * The mapping: each content column's authored width becomes its `flex` SHARE of the summed
 * authored widths, and its `minWidth` is pinned to that same authored width. Both of the
 * properties that matter fall out of MUI's flex resolution (`computeFlexColumnsWidth`, a
 * straight CSS-flexbox resolve — read it before changing this):
 *
 *  - WIDE — free space is distributed in proportion to the authored widths, so the design's own
 *    proportions survive exactly and the filler goes to zero.
 *  - NARROW — free space is smaller than the sum of the minWidths, so every column freezes at its
 *    minWidth, which IS its authored width. Rendered widths and horizontal scroll come out
 *    identical to before the change. That property is the whole reason this is safe to apply to
 *    every table at once rather than page by page.
 *
 * Deliberately NOT in scope, so this isn't mistaken for the whole of VRG-325: re-proportioning
 * individual columns (Certificates' `Scans` reserves 120px for a single `0` glyph). That is
 * per-table judgement against realistic production data; this only stops the grid abandoning
 * space it was already given.
 */
/**
 * `hiddenFields` — fields the caller has switched off via `visibilityModel`. A hidden column
 * renders at zero width but was still contributing its authored width to `totalWidth`, so every
 * VISIBLE column's share was computed against an inflated denominator and came out proportionally
 * too small.
 *
 * That was harmless while nothing hid a wide column, and it stopped being harmless with tree data:
 * `CertificatesPage` keeps its original `certificate` colDef in `columns` (the Filters drawer and
 * the CSV export both read it) but hides it, because the grouping column now renders that content.
 * Measured result before this fix — Created/Status/Scans/Owner at **20px each** and the selection
 * checkbox at **1px**, i.e. an unusable table and a §49 violation, because ~434px of denominator
 * belonged to a column drawing nothing.
 *
 * Excluding hidden columns is the correct rule independent of tree data: flex distributes the space
 * columns actually occupy, so a column occupying none has no claim on it.
 */
const withProportionalFlex = (cols: any[], hiddenFields: Set<string> = new Set()) => {
  // A table that already declares `flex` anywhere has an AUTHORED responsive layout —
  // `CompanyMembersPanel`'s email column (flex 1 beside three fixed), `InternalAnnouncementsPage`'s
  // title (flex 2). Deriving flex for its siblings would silently re-cut proportions someone chose
  // on purpose, so the whole table is left exactly as it is.
  if (cols.some((col: any) => col.flex != null)) return cols

  // The width the column renders at today — the single number this mapping is anchored to.
  // `minWidth` is folded in because MUI clamps `width` by it, so a column authored with
  // minWidth > width already renders at the minWidth and has to keep doing so.
  const authoredWidth = (col: any) =>
    Math.max(Number(col.width) || DEFAULT_COLUMN_WIDTH, Number(col.minWidth) || 0)

  // A hidden column occupies no space, so it gets no share of it (see the docblock).
  const participates = (col: any) => isContentColumn(col) && !hiddenFields.has(col.field)

  const totalWidth = cols.reduce(
    (sum: number, col: any) => (participates(col) ? sum + authoredWidth(col) : sum),
    0,
  )
  // No content columns (an all-slots table) — nothing to distribute, and dividing by it would
  // hand MUI a NaN flex.
  if (totalWidth <= 0) return cols

  return cols.map((col: any) =>
    participates(col)
      ? { ...col, flex: authoredWidth(col) / totalWidth, minWidth: authoredWidth(col) }
      : col,
  )
}

// Row hover, as ONE solid value used by both the row and its pinned cells.
//
// Pinned cells have to be opaque — the middle columns scroll underneath them — so they
// cannot simply inherit a translucent row hover. While the row used rgba and the cells
// painted their own background, the overlay landed TWICE on the pinned columns: measured
// rgb(234,235,235) against the row's rgb(245,245,245), which is the mismatch at the row's
// left and right edges. A single solid colour for both cannot drift.
const rowHoverColor = (theme: any) => (theme.palette.mode === 'dark' ? '#18181b' : '#f5f5f5')

// Selected row, and a hovered selected row — same principle as `rowHoverColor` above: ONE solid
// value per state, used by the row AND its pinned cells, because a translucent overlay cannot be
// shared with opaque cells.
//
// Measured before the fix, on a selected row: the row painted rgba(161, 161, 170, 0.16) while its
// pinned cells came out an opaque rgb(33, 33, 36) — the overlay composited against
// `--DataGrid-pinnedBackground` instead of against the row, so the row split visibly at the pinned
// boundary (grey left column, plain row body).
//
// Hovering a SELECTED row lifts it to the top surface rather than darkening it further: white in
// light, and in dark the same `#18181b` the plain row hover uses. A selected row is already the
// darkest state, so "more overlay" would read as no change at all.
const rowSelectedColor = (theme: any) => (theme.palette.mode === 'dark' ? '#212124' : '#f0f0f1')
const rowSelectedHoverColor = (theme: any) => (theme.palette.mode === 'dark' ? '#18181b' : '#ffffff')

const StyledDataGrid = styled(BaseDataGrid)(({ theme }) => ({
  // Redefine the VARIABLE on the hovered row rather than setting the cells' colour.
  // Pinned cells paint `background-color: var(--DataGrid-pinnedBackground)`, and MUI X
  // redefines that variable on hover to its own blend — so a rule targeting the cells lost
  // the specificity fight (measured: cells stayed rgb(234,235,235) against the row's
  // rgb(245,245,245)). Setting the variable means the cells' own rule resolves to our
  // colour, and no override is needed.
  '& .MuiDataGrid-row:hover': {
    '--DataGrid-pinnedBackground': rowHoverColor(theme),
    backgroundColor: rowHoverColor(theme),
  },
  // `!important` deliberately, and only here. MUI X sets the hovered pinned cell's
  // background from its own rule, which out-specifies both a cell-targeted override AND the
  // variable above (tried both; the cells stayed rgb(234,235,235) against the row's
  // rgb(245,245,245) — the overlay landing twice). Pinned cells must stay opaque because the
  // middle columns scroll underneath them, so letting the row's colour show through is not
  // an option either.
  '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
    { backgroundColor: `${rowHoverColor(theme)} !important` },
  // SELECTED rows. Three rules per state, and all three are needed: the row sets the colour and
  // the variable, the cells are cleared to transparent so nothing opaque hides the row, and the
  // pinned cells are painted explicitly because they must stay opaque (the middle columns scroll
  // under them) and MUI X's own pinned rule out-specifies the variable.
  '& .MuiDataGrid-row.Mui-selected': {
    '--DataGrid-pinnedBackground': rowSelectedColor(theme),
    backgroundColor: rowSelectedColor(theme),
  },
  '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell': { backgroundColor: 'transparent' },
  '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight':
    { backgroundColor: `${rowSelectedColor(theme)} !important` },
  // Declared AFTER the plain selected rules so it wins on order, not on added specificity.
  '& .MuiDataGrid-row.Mui-selected:hover': {
    '--DataGrid-pinnedBackground': rowSelectedHoverColor(theme),
    backgroundColor: rowSelectedHoverColor(theme),
  },
  '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell': { backgroundColor: 'transparent' },
  '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight':
    { backgroundColor: `${rowSelectedHoverColor(theme)} !important` },
  // The grid is a raised surface, like a card — so it takes `paper`, not the page tone.
  // MUI X paints pinned cells and the header/footer containers from these variables, and
  // they default to `background.default`; once the light page became tinted, that made the
  // table grey while every panel beside it was white.
  '--DataGrid-pinnedBackground': theme.palette.background.paper,
  '--DataGrid-containerBackground': theme.palette.background.paper,
  backgroundColor: theme.palette.background.paper,
  border: '1px solid',
  borderColor: theme.palette.primary[100],
  borderRadius: 12,
  // In-page list: border + radius define the card; no drop shadow (the old
  // shadow had a horizontal-offset layer that read as asymmetric/heavy).
  boxShadow: 'none',
  fontSize: 14,
  letterSpacing: 'normal',
  // REMOVE VERTICAL LINES - COLUMN BORDERS
  '& .MuiDataGrid-columnHeader': {
    borderRight: 'none',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary[100] : '#fafafa',
    color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#00000085',
  },
  '& .MuiDataGrid-cell': {
    borderRight: 'none',
    fontSize: 14,
    borderTop: `1px solid ${theme.palette.primary[100]}`,
    // Vertically center all cell content — rows are tall (72px), so short cells
    // (text, chips) would otherwise top-align next to tall cells (avatars/images).
    display: 'flex',
    alignItems: 'center',
  },
  // THE TITLE YIELDS; THE STATUS INDICATORS NEVER DO — the tree-data half of the rule
  // the page `Header` states in full (`navigation/Header/Header.tsx`, title row).
  //
  // MUI's `GridTreeDataGroupingCell` wraps the cell's content in an UNCLASSED `<span>`
  // with `white-space: nowrap`, `overflow: visible` and `min-width: auto`. That span is
  // a flex item sitting beside the expand/collapse toggle, so its automatic minimum size
  // is its MIN-CONTENT size — and because a `noWrap` title inside it reports its full
  // text width as min-content, the span cannot shrink at all. Every `minWidth: 0` in
  // `CertificateCell` is downstream of it and therefore powerless.
  //
  // What that produced, measured on /certificates at 1024w, the ZZ-ACK-06 row: the span
  // rendered 405px wide starting 36px into a 405px box, so it ran 36px past its parent
  // and the cell's `overflow: hidden` sliced the END off the row — which is exactly where
  // the integrity indicators live. 17px of the "Opened" pill was cut, and the title did
  // not ellipsize at all (`scrollWidth === clientWidth`). A half-drawn pill is worse than
  // a truncated name: the name is recoverable from the row you are about to open, and a
  // clipped indicator is indistinguishable from an absent one.
  //
  // Setting the span's `min-width` to 0 restores the intended order — verified live: the
  // span resolved to 369 (inside its box), the title ellipsised 252 -> 216, and the badge
  // landed fully inside the cell. Global rather than per-table: it is a property of MUI's
  // own wrapper, so every tree-data table has it and none of them wants the overflow.
  '& .MuiDataGrid-treeDataGroupingCell > span': {
    minWidth: 0,
  },
  // A ROW WITH NOTHING TO EXPAND PAYS NOTHING FOR THE TOGGLE (VRG-530).
  //
  // MUI renders `.MuiDataGrid-treeDataGroupingCellToggle` on EVERY row of a tree-data
  // grid, expandable or not, and it is an in-flow flex item — so a leaf reserves the
  // toggle's box and its content starts that far in. On /certificates that made the
  // first column's content sit visibly right of its own header text.
  //
  // Measured at 1280x800, before: header text "Certificate" left 332; cell image plate
  // left 368. The 36px delta is exactly the toggle — width 28 + marginRight 8 — and
  // NOT padding: the header (10 + 8) and the cell (18) already agree at 332, which is
  // also where the grouping cell itself starts. The same 36px is recorded in the
  // comment above, measured independently for the overflow bug.
  //
  // `:not(:has(button))` is what makes this derived rather than a magic offset: MUI puts
  // a real <button> in the toggle only when the row HAS children, so the space collapses
  // for leaves and is untouched for group parents. Zeroing width + marginRight (rather
  // than subtracting 36 somewhere) means it stays correct if the toggle's own size ever
  // changes. `:has()` is already relied on for the pinned-column dividers below.
  //
  // Depth indentation is unaffected — MUI indents a nested row with a marginLeft on the
  // grouping cell, not via this toggle, so a child still steps in under its parent.
  //
  // TRADE-OFF, stated because it is real: on a grid that DOES have group parents, a
  // parent keeps its 36px and a leaf no longer does, so same-depth siblings no longer
  // start at the same x. The measured /certificates dataset has no parents at all (7
  // rows, all depth 0, no toggle button), so today this is pure gain; the alternative —
  // indenting the HEADER by 36px instead, keeping every row aligned to every other but
  // pushing "Certificate" off the column's left edge — is the fix to reach for if
  // ragged sibling titles ever read worse than an indented header.
  // `flex` (not `width`) is what actually has to go: MUI sizes the toggle `flex: 0 0 28px`,
  // and on a flex item a definite `flex-basis` wins over `width` — setting `width: 0` alone
  // moved the content by only the 8px margin (measured 368 -> 360, still 28px adrift).
  '& .MuiDataGrid-treeDataGroupingCellToggle:not(:has(button))': {
    flex: '0 0 0',
    width: 0,
    minWidth: 0,
    marginRight: 0,
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    paddingLeft: 8,
  },
  // Opt-in: let a header's CONTENT box fill the cell instead of shrinking to fit.
  // MUI sizes `…TitleContainerContent` to its content (measured: 106px inside a
  // 413px container on the certificates tree column), so any `marginLeft: auto`
  // inside a `renderHeader` is inert and a "right-aligned" control renders flush
  // against the title. Set `headerClassName: 'fillHeaderContent'` on a column whose
  // header holds a right-aligned control — today only the certificates grouping
  // column's ExpandAllGroupsToggle. Deliberately opt-in rather than global: every
  // other header is left-aligned text, where filling would change nothing visible
  // but would silently alter the box every sort arrow and info glyph sits in.
  '& .MuiDataGrid-columnHeader.fillHeaderContent .MuiDataGrid-columnHeaderTitleContainerContent':
    {
      flex: 1,
      minWidth: 0,
      overflow: 'visible',
    },
  // The sort arrow is a QUIET indicator, not a call to action. MUI renders it at full
  // `inherit` weight, which made it read brighter than the header label it belongs to —
  // the arrow was drawing more attention than the column name. Dim it and give it room to
  // breathe away from the title (it sat flush against the last letter).
  '& .MuiDataGrid-iconButtonContainer': {
    marginLeft: 6,
    visibility: 'visible',
    width: 'auto',
  },
  '& .MuiDataGrid-sortIcon': {
    opacity: 0.55,
    fontSize: 18,
    color: 'inherit',
  },
  // On hover the column is interactive, so the arrow may step forward — but only to full
  // strength of the header's own muted colour, never brighter than the label.
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon': {
    opacity: 0.85,
  },
  '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
    borderBottom: 'none'
  },
  '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary[100] : '#fafafa',
    borderBottom: 'none',
    borderRight: 'none',
    borderLeft: 'none',
    borderTop: 'none',
  },
  '& .MuiDataGrid-filler': {
    backgroundColor: 'transparent',
    borderRight: 'none',
    borderLeft: 'none',
    borderTop: 'none',
    borderBottom: 'none',
  },
  '& .MuiDataGrid-filler *': {
    borderRight: 'none',
    borderLeft: 'none',
    borderTop: 'none',
    borderBottom: 'none',
  },
  // Extend the pinned-right (actions) column's left divider through the empty
  // filler area below the rows, so the line reaches the bottom of the table
  // instead of stopping at the last row. (Declared after the filler reset above
  // so it wins on equal specificity.)
  '& .MuiDataGrid-filler--pinnedRight': {
    borderLeft: `1px solid ${theme.palette.primary[100]}`,
  },
  // Same treatment for the pinned-LEFT column's right divider — parity with the
  // right side, so both frozen-column boundaries reach the table bottom rather
  // than the left one dangling at the last row. (Empty tables suppress both via
  // the `isEmptyTable` sx so no stray line floats beside the empty state.)
  '& .MuiDataGrid-filler--pinnedLeft': {
    borderRight: `1px solid ${theme.palette.primary[100]}`,
  },
  '& .MuiDataGrid-iconSeparator': {
    display: 'none',
  },
  // CELL OUTLINES ON FOCUS
  '& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within' : {
    outline: 'none'
  },
  // CELL PADDING
  '& .MuiDataGrid-cell--textLeft': {
    paddingLeft: 18,
  },
  // LEFT GUTTER (standing rule, VERA-790 revised): the first *non-pinned* data column gets a left
  // gutter so its content clears the frozen-column divider (or the table's left border when nothing
  // is pinned). It is tagged `.firstDataCol` in `parsedColumns` above — NOT `:first-of-type`, which
  // (with a pinned/utility column leading) landed the gutter on the pinned column and broke the
  // pinned divider. The pinned column keeps its own padding; the gutter only ever lands here.
  '& .MuiDataGrid-columnHeader.firstDataCol .MuiDataGrid-columnHeaderTitleContainer': {
    justifyContent: 'flex-start',
    paddingLeft: 24,
  },
  '& .MuiDataGrid-cell.firstDataCol': {
    paddingLeft: 24,
  },
  // PINNED COLUMN SHADOWS - REMOVED
  '& .MuiDataGrid-cell--withRightBorder': {
    boxShadow: 'none',
    borderRightColor: theme.palette.primary[100],
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
  },
  '& .MuiDataGrid-cell--withLeftBorder': {
    boxShadow: 'none',
    borderLeftColor: theme.palette.primary[100],
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
  },
  '& .MuiDataGrid-columnHeader--withRightBorder': {
    borderRightColor: theme.palette.primary[100],
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
  },
  '& .MuiDataGrid-columnHeader--withLeftBorder': {
    borderLeftColor: theme.palette.primary[100],
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
  },
  // MUI computes `--withRightBorder`/`--withLeftBorder` in a layout pass that runs
  // AFTER a row/column first mounts — on the very first paint of freshly-arrived
  // rows (the loading→loaded transition, before that pass has run), the pinned
  // column's boundary can render with no divider for a frame, then self-correct.
  // This is a CSS-only, always-on backstop that doesn't depend on that timing at
  // all: `:has()` picks out exactly the LAST pinned-left cell/header (the one
  // immediately followed by a non-pinned sibling) and always borders it, so
  // there's no window where the boundary can render blank.
  '& .MuiDataGrid-cell--pinnedLeft:has(+ .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft))': {
    borderRightColor: theme.palette.primary[100],
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
  },
  '& .MuiDataGrid-columnHeader--pinnedLeft:has(+ .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--pinnedLeft))': {
    borderRightColor: theme.palette.primary[100],
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
  },
  // VERA-914: bulk-select checkbox column injection. MUI mounts the checkbox column
  // at its full width instantly (it can't animate a column insert from 0 width), so the
  // data rows snap. The pinned-left FILLER, however, has its width set inline to the
  // pinned-group width, so a `width` transition there made it GLIDE while the rows
  // snapped — the "delayed border below the rows" desync. There is no CSS-only way to
  // make MUI's column injection actually slide, so instead of a half-animation we keep
  // the whole pinned divider moving as ONE instant unit: no width transitions anywhere
  // on the pinned columns. (The old `transition: '--width …'` here was a no-op typo —
  // it targeted a non-existent CSS custom property, not `width`.)
  '& .MuiDataGrid-cellCheckbox': {
    paddingLeft: 8,
  },
  // CHECKBOX
  // TODO: FIX CHECKBOX WIDTH
  '& .MuiDataGrid-checkboxInput': {
    svg: {
      path: {
        // Checkbox follows the theme: white on the dark surface, but GREY in light
        // mode (white boxes were invisible on the light table background).
        fill: theme.palette.mode === 'light' ? theme.palette.text.secondary : theme.palette.common.white,
        padding:"4px",
        stroke: theme.palette.mode === 'light' ? theme.palette.text.secondary : theme.palette.common.white,
        strokeWidth: '0.05px !important',
      },
    }
  },
  '& .MuiDataGrid-checkboxInput svg': {
    fill: theme.palette.mode === 'light' ? theme.palette.common.white : 'black',
    strokeWidth: '0.05px !important',
  },
  // VRG-474 — the unchecked-checkbox rule that used to live here is GONE, and
  // deliberately not replaced. It set `fill` AND `stroke` on the icon's `path`, and the
  // stroke is what made the border read as heavy: MUI's `CheckBoxOutlineBlank` is a
  // filled ring 2 viewBox units thick, so stroking its outline at width 1 straddled both
  // contours and rendered a ~3px border (2.5..5.5 in viewBox units) where every field on
  // the page draws 1px. That was the "checkboxes are too bold" complaint.
  //
  // The unchecked box is now a real 1px stroked rect owned by the shared theme
  // (`packages/ui/theme/checkboxIcon.tsx` + `MuiCheckbox` in `theme.ts`), and the
  // muted-unchecked / solid-checked split this rule was tuning moved there with it — so
  // modal and list checkboxes get the same treatment instead of only the tables, and
  // there is ONE place to change it (§47: improve layer 1, don't re-specify per site).
  //
  // Do not reintroduce a per-site colour here. If a checkbox looks wrong, fix the theme.
  //
  // ⚠️ SELECTOR NOTE, still true and still worth reading: `.MuiDataGrid-checkboxInput`
  // matches ZERO elements in this DataGrid version — verified in the browser
  // (`querySelectorAll('.MuiDataGrid-checkboxInput').length === 0` while
  // `.MuiCheckbox-root` returned 19). The rules above and below that target it are dead
  // CSS; they are left alone rather than swept up blind, but do not copy them as a
  // pattern. Confirm your selector matches something before believing a change worked.
  '& .MuiDataGrid-checkboxInput.Mui-checked svg path': {
    fill: theme.palette.mode === 'light' ? theme.palette.text.secondary : theme.palette.common.white,
    stroke: theme.palette.mode === 'light' ? theme.palette.text.secondary : theme.palette.common.white,
  },
  // HIDE ROW SELECT COUNT
  '& .MuiDataGrid-selectedRowCount': {
    display: 'none'
  },
  // Hide only MUI's empty leftover footer node (direct child). The previous
  // descendant selector (`footerContainer div:first-of-type`) also matched the
  // custom RowsPerPageSelector's inner Select wrapper, hiding the page-size
  // dropdown. Scope it to the direct child so the selector renders bottom-left.
  '& .MuiDataGrid-footerContainer > div:first-of-type': {
    display: 'none'
  },
  // FOOTER BORDER COLOR
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.primary[100]}`,
  },
  '& .MuiDataGrid-booleanCell[data-value="true"]' :{
      // color: `${theme.palette.brand[500]}`,
  },
  // HIDE SCROLLBARS
  '& *': {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
      width: 0,
      height: 0,
    },
    '&::-webkit-scrollbar-track': {
      display: 'none',
    },
    '&::-webkit-scrollbar-thumb': {
      display: 'none',
    },
  },
  '& .MuiDataGrid-scrollbarFiller': {
    display: 'none !important',
  },
  '& .MuiDataGrid-scrollbarFiller--header': {
    display: 'none !important',
  },
  '& .MuiDataGrid-scrollbarFiller--pinnedRight': {
    display: 'none !important',
  },
  '& .MuiDataGrid-scrollbarFiller--pinnedLeft': {
    display: 'none !important',
  },
  // HIDE OVERFLOW MENU DOTS
  '& .MuiDataGrid-menuIcon': {
    display: 'none !important',
  },
  '& .MuiDataGrid-menuIconButton': {
    display: 'none !important',
  },
}));

// CUSTOM FOOTER
function CustomFooter() {
  const apiRef = useGridApiContext();
  const page =  useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const totalRowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);

  return (
    <>
      <RowsPerPageSelector
        apiRef={apiRef}
        pageSize={pageSize}
        pageCount={pageCount}
      />
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body3" color="text.secondary">
            {formatPageRange(page, pageSize, totalRowCount)}
        </Typography>
        {/* VRG-703 — the grid's page is 0-based and MUI's Pagination is 1-based. Passing the
            grid page straight across (as this did since 1aaef738c) left page 1 with no
            highlighted button and highlighted "1" on page 2. */}
        <Pagination
          sx={{marginLeft: 2, pr: 5}}
          page={toPaginationButtonPage(page)}
          count={pageCount}
          onChange={(value: number) => {
            return apiRef.current.setPage(toGridPage(value))
          }}
        />
      </Stack>
  </>
  );
}

// CUSTOM LOADER — a column-aware skeleton overlay (rows of placeholder cells
// sized to the visible columns + current density) instead of a spinner, so the
// loading state previews the shape of the table. Falls back to the branded
// Loader if grid dimensions aren't available yet.
function CustomLoader() {
  const apiRef = useGridApiContext()
  const columns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector)
  const dimensions = useGridSelector(apiRef, gridDimensionsSelector)

  const rowHeight = dimensions?.rowHeight || 52
  const viewportHeight = dimensions?.viewportInnerSize?.height || 480
  if (!columns || columns.length === 0) {
    return <Loader />
  }
  const rowCount = Math.max(5, Math.min(12, Math.floor(viewportHeight / rowHeight)))

  return (
    <Box
      sx={{
        // `position: relative` + 100%, NOT `position: absolute; inset: 0` — which is what this was,
        // and it clipped the ENTIRE overlay away on every table in the app.
        //
        // MUI renders this slot inside `.MuiDataGrid-overlayWrapperInner`, which carries an inline
        // `height`/`width` (measured 1405x858 on /owners/:id) — but its PARENT,
        // `.MuiDataGrid-overlayWrapper`, is `position: sticky` at 0x0. `inset: 0` resolves against
        // the nearest POSITIONED ancestor, so it resolved against that 0x0 box, and `overflow:
        // hidden` then clipped all 77 skeleton bars to nothing. The rows still laid out — every
        // `getBoundingClientRect()` reported real 14px bars at real row pitch (72px) — so this was
        // invisible to measurement and only showed up in a screenshot: a table whose loading state
        // painted an empty grid body.
        //
        // Percentages resolve against the PARENT's content box (the sized inner wrapper), so 100%
        // is the one form that gets the real height here. `CustomEmptyState` directly below already
        // used `width/height: 100%` and has always rendered correctly — the working pattern was
        // sitting next to the broken one.
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        zIndex: 4,
      }}
    >
      {Array.from({ length: rowCount }).map((_, r) => (
        <Stack
          key={`skeleton-row-${r}`}
          direction="row"
          alignItems="center"
          sx={{
            height: rowHeight,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {columns.map((col: any, c: number) => (
            <Box
              key={col.field}
              sx={{ width: col.computedWidth || col.width || 100, px: 2, flexShrink: 0 }}
            >
              <Skeleton variant="rounded" height={14} width={`${45 + ((r + c) % 4) * 13}%`} />
            </Box>
          ))}
        </Stack>
      ))}
    </Box>
  )
}

const HiddenColumnHeaders = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function HiddenColumnHeaders(_props, _ref) {
    return null
  }
)

// (List view temporarily disabled until we properly wire a renderGridCell prop)

interface TableProps {
  columns: any
  rows: any
  pinnedRows?: any
  getRowClassName?: any
  getRowId?: (row: any) => string | number
  loading?: boolean
  showActionLine?: boolean
  showSearch?: boolean
  search?: string
  onSearchChange?: (value: string) => void
  // VERA-504, corrected by VRG-265 — the scan control BESIDE the search input (outside it). It is
  // a target selector sharing one slot with the filter fields' selectors: lit means scans land in
  // the search box, and only one control is ever lit. Opt-in per surface; see
  // `ActionLine.showScan`.
  showScan?: boolean
  /** Entity a toolbar scan resolves to. See ActionLine.scanEntity. */
  scanEntity?: ScanFilterEntity
  bulkActions?: any
  onSelectionModelChange?: (ids: (string | number)[]) => void
  actionLineRightComponents?: React.ReactNode
  /**
   * The toolbar's LEFT group, after the search field (ActionLine `afterSearchComponents`). For a
   * page that keeps its own search and filters above the table (`showSearch={false}`), this is
   * where a count or a view toggle goes, so the toolbar row is not an empty band beside its
   * buttons (VRG-734).
   */
  actionLineLeftComponents?: React.ReactNode
  showSort?: boolean
  sort?: any
  showFilters?: boolean
  filters?: any
  // When provided, the filters drawer reports applied filters to the parent so it can
  // drive SERVER-side filtering. Omitted by default → drawer stays client-side only.
  onFiltersChange?: (filters: { field: string; operator?: string; value: unknown }[]) => void
  extraFilters?: React.ReactNode
  onFiltersApply?: () => void
  extraFiltersDirty?: boolean
  onClearExtraFilters?: () => void
  showLayoutOptions?: boolean
  renderGridCell?: any
  layout?: string
  density?: any
  topOffset?: number
  // Full-page tables: fill so the table's bottom gap matches the page L/R gutter
  // (24px). Leave off for tables that share the page with content below the grid.
  fillBottom?: boolean
  // Explicit outer height (e.g. a fixed px or `calc(...)`). Overrides both
  // fillBottom and topOffset. Use for embedded-panel tables that should NOT span
  // the full viewport (e.g. a side-by-side members panel), so their bottom bar
  // stays clearly in view instead of being pinned to the viewport edge.
  height?: number | string
  // Fill the parent's height via pure CSS flex (no JS measurement, no magic
  // numbers): the outer box becomes `height:100%` flex-column and the grid
  // `flex:1`, so the grid's bottom = the parent's bottom exactly. The parent
  // must give it a definite height (e.g. a flex:1 row). Opt-in — default off, so
  // every existing fillBottom/topOffset table is unchanged.
  fillParent?: boolean
  visibilityModel?: any
  showSelection?: boolean
  selectionDisabled?: boolean
  alwaysShowRowSelection?: boolean
  showColumnHeaders?: boolean
  showFooter?: boolean
  onDensityChange?: (density: any) => void
  emptyState?: React.ReactNode
  sx?: object
  // Filtering: 'server' tells the grid the rows are already filtered (don't client-filter) — pair
  // with onFilterModelChange to send the model to the API. Default 'client' (unchanged behavior).
  filterMode?: 'client' | 'server'
  // Pagination (server/client)
  paginationMode?: 'client' | 'server'
  rowCount?: number
  /**
   * VRG-703 — with `onPaginationModelChange`, the CALLER owns the page (controlled). Without
   * it, `paginationModel` is only the starting page/size and the grid pages itself. It used
   * to be passed through as controlled either way, so `paginationModel={{ page: 0, pageSize:
   * 25 }}` alone pinned the table to page 0: the buttons highlighted and nothing moved.
   * Either way, changing a sort or filter goes back to page 1.
   */
  paginationModel?: { page: number; pageSize: number }
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void
  /** VRG-703 — rows per page to start on when the grid owns paging (25, 50, 100 or -1). */
  initialPageSize?: number
  // Sort (server-side)
  sortingMode?: 'client' | 'server'
  onSortModelChange?: (model: GridSortModel) => void
  /**
   * VRG-227 — for a `paginationMode="server"` table, fetch ONE page of rows matching the
   * table's CURRENT filters/search (the same query params the caller already uses to drive
   * its own `rows` prop, just with a different limit/offset). Table drives the paging loop
   * itself when Download is clicked — batching `EXPORT_BATCH_SIZE`-row requests up to a
   * `MAX_EXPORT_ROWS` ceiling — so the exported file covers every row matching the query
   * instead of just the page currently loaded into the grid.
   *
   * Omit this for a server-paginated table that hasn't been wired up yet: Download still
   * works, exporting only the loaded page (unchanged from before this fix), and the button's
   * tooltip is worded to match — it never claims a full export it can't deliver. Never needed
   * for `paginationMode="client"`: the grid already holds every row there, so the existing
   * loaded-rows export already IS the full export.
   */
  fetchExportPage?: (params: { limit: number; offset: number }) => Promise<{ rows: any[]; total: number }>
  // The order the data ALREADY arrives in, so the grid can show it.
  //
  // Two different questions, two different affordances: the column header shows STATE
  // ("what order is this in, and why is this row here?"), while the Sort menu's badge shows
  // DEVIATION ("you changed something from the default"). Without this, a table sorted by
  // the server rendered with no header indicator at all, so the order looked arbitrary —
  // and if the default were pushed through `sort` instead, the badge would be permanently
  // lit and would stop meaning anything.
  defaultSortModel?: GridSortModel
  // Row selection control
  rowSelectionModel?: GridRowSelectionModel
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void
  // Per-user saved views (Jira-like). When set, renders the saved-views bar and
  // persists filter/sort/column/density to the user's preferences under this key.
  /** Filename base (no extension) for the CSV export. Defaults to 'export'. */
  exportFilename?: string
  /** Hide the CSV download control on this table. */
  hideExport?: boolean
  viewKey?: string
  // Callback fired whenever the internal filter model changes (e.g. after user
  // clicks Apply in the Filter side panel). Useful for callers that need to react
  // to client-side filter state — e.g. to derive a server param from a filter
  // value (CompaniesPage uses this to set showArchived from the Status filter).
  onFilterModelChange?: (model: GridFilterModel) => void
  // VRG-236 — opt-in inline row expand, built on DataGridPro's row Detail Panel
  // feature (NOT tree data — see the file-level note above `StyledDataGrid` for
  // why). Both are OMITTED for every existing caller, so `<StyledDataGrid>`
  // receives no detail-panel props at all unless a caller opts in: every table
  // that doesn't ask for this renders byte-identically to before.
  //
  // When a row's `getDetailPanelContent` returns non-null, DataGridPro
  // auto-inserts a leading chevron-toggle column (field
  // `GRID_DETAIL_PANEL_TOGGLE_FIELD`). Clicking it expands an inline panel below
  // the row with whatever `getDetailPanelContent` returns. This is why it's the
  // right primitive for "chevron on group PARENT rows only, expanding to show
  // children inline": the children are never part of `rows` (so they can never
  // double as separate top-level rows, and are outside sort/filter/pagination/
  // selection).
  //
  // VRG-231 review round 2 item 3 — DataGridPro's OWN default toggle cell
  // (`GridDetailPanelToggleCell`) does NOT omit the chevron for rows without
  // panel content — it renders the chevron on every row and only sets
  // `disabled` on it (see the package source: `disabled: !hasContent`). A
  // disabled-but-visible chevron on every row read as broken (it renders, it
  // just silently does nothing when clicked on a non-group row). So below,
  // when a caller opts into `getDetailPanelContent`, a custom column definition
  // for `GRID_DETAIL_PANEL_TOGGLE_FIELD` is injected into `columns` that
  // renders NOTHING (not even a disabled button) for a row whose
  // `getDetailPanelContent` returns null/undefined — real toggle cell for
  // group parents, nothing at all for every other row.
  //
  // Community fallback (no `hasMuiXLicense`): `getDetailPanelContent` /
  // `getDetailPanelHeight` are DataGridPro-only props. `DataGridCommunity`
  // silently ignores props it doesn't declare (it destructures its own prop
  // list; it never spreads unknown props onto the DOM), so passing them here
  // is inert — no crash, no console error, just no toggle column and no panel.
  // Callers that want a non-expandable equivalent (e.g. a static member-count
  // badge) render it themselves in the cell; Table has no opinion on that.
  getDetailPanelContent?: (params: any) => React.ReactNode
  // Defaults to `() => 'auto'` (content-sized) when `getDetailPanelContent` is
  // set and this is omitted — DataGridPro's own built-in default is a fixed
  // 500px, which is wrong for a handful of member rows.
  getDetailPanelHeight?: (params: any) => number | 'auto'
  // VRG-231 — opt-in DataGridPro Tree Data, replacing the row Detail Panel above
  // for "expand a parent row into REAL child rows" (a group's tagged children).
  // A Detail Panel is a free-form drawer slotted BELOW a row — it structurally
  // cannot carry its own columns, a selection checkbox, or align to the header,
  // and it expands via a height swap that reads as a flash rather than an
  // animated reveal. Tree Data children are ordinary rows: every column renders
  // for them, they get the same bulk-select checkbox as any row, and MUI
  // animates the reveal.
  //
  // `rows` must already contain every child row (with the same shape as a
  // top-level row) alongside the top-level rows — Table does not fetch or merge
  // them; that's the caller's job (see CertificatesPage's `useQueries` fetch +
  // merge, keyed on the same `['certificates', 'group', id]` query other cells
  // on the same page already use, so it's a cache hit rather than a new
  // request). `getTreeDataPath` maps a row to its path from the root — a
  // top-level row is `[row.id]`; a child is `[row.parent_certificate_id,
  // row.id]`.
  //
  // Community fallback (no `hasMuiXLicense`): DataGridCommunity has NO tree-data
  // support at all (unlike the detail-panel props above, which it silently
  // ignores while still rendering harmlessly). Passing child rows straight
  // through would render every one of them as an ordinary flat top-level row —
  // double-counted in the footer, in bulk selection, in CSV export. So when
  // there's no Pro license, Table trims `rows` down to top-level entries only
  // (`getTreeDataPath(row).length === 1`) and never passes `treeData` /
  // `getTreeDataPath` / `groupingColDef` to the grid at all. A table that
  // doesn't opt in (the vast majority) is completely unaffected either way.
  treeData?: boolean
  getTreeDataPath?: (row: any) => string[]
  // The column DataGridPro auto-inserts to render the tree (indentation +
  // expand/collapse toggle) — see MUI's own `GridGroupingColDefOverride`. The
  // caller supplies `renderCell`/`valueFormatter`/`headerName`/`width` etc.;
  // Table only handles pinning it (alongside the checkbox column, in the same
  // leftmost slot the caller's own pinned-left data column used to occupy) and
  // wiring the expand/collapse glyphs to the app's lucide iconography.
  groupingColDef?: any
  // Product-tour anchor id, applied as `data-tour` on the table's outer box so a
  // tour step can spotlight the whole list — toolbar and grid together. Opt-in
  // rather than automatic: a page can hold more than one Table, and an anchor id
  // has to resolve to exactly one element. See working-conventions §42.
  dataTour?: string
}

export const Table = (props: TableProps) => {
  const {
    rows: rawRows,
    pinnedRows,
    columns,
    getRowClassName,
    getRowId,
    loading = false,
    showActionLine = true,
    showSearch = true,
    search,
    onSearchChange,
    showScan = false,
    scanEntity,
    bulkActions,
    actionLineRightComponents,
    actionLineLeftComponents,
    extraFilters,
    onFiltersApply,
    extraFiltersDirty,
    onClearExtraFilters,
    showFilters = true,
    onFiltersChange,
    showSort = true,
    showLayoutOptions = true,
    layout = 'table',
    renderGridCell,
    density = 'standard',
    topOffset = false,
    fillBottom,
    height: heightOverride,
    fillParent = false,
    visibilityModel = {},
    showSelection = true,
    selectionDisabled = true,
    alwaysShowRowSelection = false,
    showColumnHeaders = true,
    showFooter = true,
    onDensityChange,
    emptyState,
    sx = {},
    filterMode = 'client',
    paginationMode = 'client',
    rowCount,
    paginationModel,
    onPaginationModelChange,
    initialPageSize,
    sortingMode = 'client',
    onSortModelChange: externalOnSortModelChange,
    fetchExportPage,
    defaultSortModel,
    rowSelectionModel: externalRowSelectionModel,
    onRowSelectionModelChange: externalOnRowSelectionModelChange,
    exportFilename,
  hideExport = false,
  viewKey,
    onFilterModelChange: externalOnFilterModelChange,
    getDetailPanelContent,
    getDetailPanelHeight,
    treeData = false,
    getTreeDataPath,
    groupingColDef,
    dataTour,
  } = props

  let {
    filters = [],
  } = props
  const { sort = [] } = props

  // VRG-231 — see the `treeData` prop doc above: DataGridCommunity has no tree-data
  // support at all, so a caller opting in still hands `rawRows` every child row
  // (Table doesn't fetch/merge them) — trim to top-level-only here rather than let
  // Community render children as ordinary flat rows.
  const rows =
    treeData && !hasMuiXLicense && getTreeDataPath
      ? (rawRows as any[]).filter((row) => getTreeDataPath(row).length === 1)
      : rawRows

  const theme = useTheme()
  const { open: notification } = useSnackbar()

  // for table width setting for loader
  const tableRef = useRef(null)
  const [width, setWidth] = useState(0);

  // QUERY STRING PARSING
  const [searchParams, setSearchParams] =  useSearchParams();
  const filterParams = searchParams.get('filters')
  const sortParams = searchParams.get('sort')

  if (filterParams) {
    filters = filterParams?.split('&').map(filter => {
      const [field, operator, value] = filter.split(':')
      return {
        field: field,
        operator: operator,
        value: value
      }
    })
  }

  // VRG-227 — `?sort=field:direction[&field2:direction2]`, mirroring the `filters` param's
  // own `field:operator:value` shape immediately above (just with no operator segment: a
  // sort clause only ever HAS a field and a direction). `sortParams` above used to be read
  // and never referenced again — this was a genuine dead end, not a stylistic gap: a link
  // promising a ranking ("scanned most in France") would apply the filter and silently keep
  // the table's default order, so the destination looked correct while the one thing the
  // link was actually about was wrong.
  //
  // Validated against THIS table's own `columns`, not handed to the grid blind — MUI's
  // DataGrid has no defence against an unknown sort field, so a hand-edited or stale URL
  // must degrade to the default order rather than throw or blank the grid. An entry with no
  // colon, an unknown field, or a direction that isn't exactly `asc`/`desc` is silently
  // dropped (not partially applied).
  //
  // This is why `PeoplePage`'s own pre-existing `?sort=<field>&?order=<asc|desc>` convention
  // (two separate params, read directly into its own server query — PeoplePage.tsx:127-128)
  // is UNAFFECTED: its `sort` value (e.g. `scans`) carries no colon, so `clause.split(':')`
  // yields no direction and the entry is dropped here every time, regardless of whether
  // `scans` happens to also be one of this table's column fields. PeoplePage never passes a
  // `sort` prop to this component, so this parsed value is never even consulted for it —
  // noted for the record, not required for correctness. That leaves this table and
  // PeoplePage's own field+order pair as two genuinely separate mechanisms reading the same
  // `sort` param name for two different shapes; they don't collide today, but a future page
  // should pick ONE of the two conventions rather than adopt both.
  let urlSortModel: GridSortModel | undefined
  if (sortParams) {
    const validSortFields = new Set((columns ?? []).map((col: any) => col.field))
    const parsed: GridSortModel = sortParams
      .split('&')
      .map((clause) => {
        const [field, direction] = clause.split(':')
        if (!field || !validSortFields.has(field)) return null
        if (direction !== 'asc' && direction !== 'desc') return null
        return { field, sort: direction as 'asc' | 'desc' }
      })
      .filter((entry): entry is { field: string; sort: 'asc' | 'desc' } => entry !== null)
    if (parsed.length) urlSortModel = parsed
  }

  // SETTING THE TABLE
  const apiRef = useGridApiRef();
  const pagination = resolvePagination(paginationModel, onPaginationModelChange, initialPageSize);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: filters,
    quickFilterValues: [search],
    quickFilterLogicOperator: GridLogicOperator.Or,
  });

  const [showRowSelection, setShowRowSelection] = useState(alwaysShowRowSelection);
  // VRG-227 — true while Download is paging through `fetchExportPage` to assemble a
  // full-result-set export. Drives the Download button's spinner + disabled state so a
  // second click can't start an overlapping fetch loop.
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Layout transitions are OFF for the first paint.
   *
   * The pinned columns carry `transition: left/width 300ms` so the bulk-select checkbox
   * can slide the whole pinned divider as one unit (VERA-914). That is right for an
   * INTERACTION and wrong for the initial render: MUI computes each pinned column's
   * `left` after mount, so the very first assignment is itself a change and the browser
   * animates it — every table header visibly slid in from the left on page load, on every
   * list in the app. It read as a deliberate entrance animation nobody asked for.
   *
   * Enabled on the frame AFTER mount, so the first layout lands instantly and every
   * subsequent change still glides. rAF rather than a plain effect because the effect can
   * run before the browser has painted the initial `left`, which would leave the
   * transition armed for exactly the change we are trying not to animate.
   */
  const [animateLayout, setAnimateLayout] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimateLayout(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const [internalRowSelectionModel, setInternalRowSelectionModel] = useState<GridRowSelectionModel>([]);
  
  // Use external row selection model if provided, otherwise use internal state
  const rowSelectionModel = externalRowSelectionModel ?? internalRowSelectionModel;
  const setRowSelectionModel = externalOnRowSelectionModelChange ?? setInternalRowSelectionModel;
  
  // Seeded from defaultSortModel so the header indicator matches the order the rows
  // actually arrive in. Precedence, highest first:
  //   1. `sort` (an explicit caller-controlled model) — a caller that hands this in is
  //      deliberately forcing the initial state, a stronger and more specific signal than
  //      whatever happens to be sitting in the URL, so it still wins outright.
  //   2. the URL's `?sort=` param (`urlSortModel`, parsed above) — a real request from
  //      whoever followed this link (a saved bookmark, a share, an Ask Vera "show this
  //      ranking" deep link) to see a DIFFERENT order than the page's own default.
  //   3. `defaultSortModel` — a description of the order the data already arrived in
  //      (typically the server's own default), not a preference; it's what's left once
  //      nothing more specific asked for something else.
  // Read once, at mount, via this initial useState value — same as `filters` above.
  // Deliberately NOT re-read on every render: once the user (or the grid's own controlled
  // `onSortModelChange` below) changes the sort, their choice must not be fought by a stale
  // URL on the next render, and the URL is never written back from here (that would add a
  // history entry per header click, a separate feature this doesn't take on).
  const [sortModel, setSortModel] = useState(sort?.length ? sort : (urlSortModel ?? defaultSortModel ?? []));

  // Is the current order still the default? Drives the Sort menu's "applied" badge, which
  // must mean "you deviated", not merely "a sort exists".
  const isDefaultSort = useMemo(
    () => JSON.stringify(sortModel ?? []) === JSON.stringify(defaultSortModel ?? []),
    [sortModel, defaultSortModel],
  );

  const [densityModel, setDensityModel] = useState(density);

  const [layoutModel, setLayoutModel] = useState(layout);


  const [columnVisibilityModel, setColumnVisibilityModel] = useState(visibilityModel)

  // Serialized snapshot of the current view, for saved views + restore-on-login.
  const currentViewState = useMemo<TableViewState>(() => ({
    filterModel,
    sortModel,
    columnVisibilityModel,
    density: densityModel,
  }), [filterModel, sortModel, columnVisibilityModel, densityModel])

  // Apply a saved view's state back onto the (controlled) grid.
  const applyViewState = (state: TableViewState | undefined) => {
    setFilterModel(
      state?.filterModel ?? {
        items: [],
        quickFilterValues: [search],
        quickFilterLogicOperator: GridLogicOperator.Or,
      },
    )
    setSortModel(state?.sortModel ?? defaultSortModel ?? [])
    setColumnVisibilityModel(state?.columnVisibilityModel ?? {})
    if (state?.density) setDensityModel(state.density)
  }

  // Reset the Columns menu's concerns to their defaults: column ORDER (back to the
  // `columns` prop order — order isn't in TableViewState, so it's restored via the
  // grid's own state), VISIBILITY (the `visibilityModel` default), and DENSITY.
  const resetColumns = () => {
    const liveFields: string[] =
      apiRef.current?.getAllColumns?.().map((c: any) => c.field) ?? []
    // Rank every field by its position in the default `columns` order (checkbox
    // first when row selection is on); unknown/internal fields sort to the end,
    // keeping their relative order.
    const rank = new Map<string, number>([
      ['__select__', -1] as [string, number],
      ...columns.map((c: any, i: number) => [c.field, i] as [string, number]),
    ])
    const orderedFields = [...liveFields].sort(
      (a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999),
    )
    apiRef.current?.restoreState({ columns: { orderedFields } })
    setColumnVisibilityModel(visibilityModel ?? {})
    setDensityModel(density ?? 'standard')
  }

  // Global "reset to default": clears filter + sort on top of the column reset.
  const resetAll = () => {
    resetColumns()
    setSortModel([])
    setFilterModel({
      items: [],
      quickFilterValues: [search],
      quickFilterLogicOperator: GridLogicOperator.Or,
    })
  }

  const leftPinnedColumns = useMemo(() => columns.filter((column: any) => column.pinned == 'left'), [columns]).map((col: any) => col.field)
  const rightPinnedColumns = useMemo(() => columns.filter((column: any) => column.pinned == 'right'), [columns]).map((col: any) => col.field)

  // LEFT GUTTER (VERA-790, revised): give the first *non-pinned* data column a left gutter so its
  // content clears the frozen-column divider (or the table's left border when nothing is pinned).
  // The pinned column(s) keep their own padding — the gutter never lands on a pinned column. Tagged
  // by field (robust vs. fragile nth-child CSS across pinned/offset fillers); styled via `.firstDataCol`.
  const firstDataField = columns.find((c: any) => c.pinned !== 'left' && c.pinned !== 'right')?.field

  // add non sort and filter to pinned columns and make all otehr columns left aligned
  const parsedColumns = columns.map((col: any) => {
    const normalizedHeaderName = col.headerName === false ? '' : col.headerName
    const withDateTimeGetter =
      col.type === 'dateTime' && !col.valueGetter
        ? { valueGetter: (value: any) => (value ? new Date(value) : value) }
        : {}
    // Un-formatted `type: 'dateTime'` columns were falling back to the browser's
    // raw `toLocaleString()` ("7/16/2026, 9:21:08 PM") — relative for the first
    // 24h ("43 minutes ago"), the house absolute format after. Respects any
    // column that already brings its own valueFormatter.
    const withDateTimeFormatter =
      col.type === 'dateTime' && !col.valueFormatter
        ? { valueFormatter: (value: any) => (value ? relativeOrDateTime(value) : '-') }
        : {}
    // Two states, not three. MUI's default cycle is asc -> desc -> null, but "null" does
    // not mean unsorted here: the rows always come back in SOME order (the server's
    // default), so the third click silently dropped the header indicator while the table
    // stayed ordered — the indicator would claim nothing was sorted when something always
    // is. Reverting to the default is a real action, but it belongs to the Sort control's
    // explicit clear (×), not to a third header click that looks like "off".
    //
    // Dates lead with DESC because the useful first answer for a timestamp is "most
    // recent", not "oldest" — clicking Encoded should show what was just encoded.
    const withSortingOrder = col.sortingOrder
      ? {}
      : { sortingOrder: col.type === 'dateTime' ? ['desc', 'asc'] : ['asc', 'desc'] }
    // `type: 'currency'` (or 'price') → format the raw number as a currency string
    // with thousands separators (e.g. 5200 → "$5,200.00") via the shared toDollar.
    // Inferred from the column type so consumers don't hand-format each price cell.
    // Mapped to MUI's numeric type for correct sorting; null/'' render as "-".
    const withCurrencyFormatter =
      (col.type === 'currency' || col.type === 'price') && !col.valueFormatter
        ? {
            type: 'number',
            valueFormatter: (value: any) =>
              value === null || value === undefined || value === ''
                ? '-'
                : toDollar(Number(value)),
          }
        : {}
    if (col.pinned) {
      // A right-pinned column with no header is the row-actions kebab — chrome, not
      // data. MUI still gave it a sort affordance, so hovering the empty header
      // showed a "Sort" tooltip and an arrow offering to sort rows by a column that
      // holds no value. Forced off here rather than per page: every list defines its
      // own `actions` column and they'd each have to remember. A pinned column that
      // DOES carry a header is real data and keeps its sorting.
      const isRowActionsColumn = col.pinned === 'right' && !normalizedHeaderName

      return {
        disableReorder: true,
        // Pinned-LEFT columns default to left-aligned header + cell (parity with the
        // non-pinned columns below); pinned-right (actions) keep their own alignment.
        // A column can still override by setting align/headerAlign explicitly (…col wins).
        ...(col.pinned === 'left' ? { align: 'left', headerAlign: 'left' } : {}),
        ...(isRowActionsColumn ? { sortable: false, filterable: false, hideable: false } : {}),
        ...col,
        ...withDateTimeGetter,
        ...withDateTimeFormatter,
        ...withSortingOrder,
        ...withCurrencyFormatter,
        headerName: normalizedHeaderName,
        width: col.width,
        // After ...col so a page can't accidentally re-enable it by spreading a
        // shared column def that sets `sortable: true`.
        ...(isRowActionsColumn ? { sortable: false } : {}),
      }
    }
    const isFirstData =
      col.field === firstDataField &&
      typeof col.cellClassName !== 'function' &&
      typeof col.headerClassName !== 'function'
    return {
      align: 'left',
      headerAlign: 'left',
      ...col,
      ...withDateTimeGetter,
      ...withDateTimeFormatter,
      ...withSortingOrder,
      ...withCurrencyFormatter,
      headerName: normalizedHeaderName,
      ...(isFirstData
        ? {
            cellClassName: `${col.cellClassName ?? ''} firstDataCol`.trim(),
            headerClassName: `${col.headerClassName ?? ''} firstDataCol`.trim(),
          }
        : {}),
    }
  })

  // VERA-914: a fully custom, ALWAYS-MOUNTED select column. MUI's built-in checkbox
  // column can't be width-controlled (it renders a fixed width regardless of overrides),
  // so we render our own checkbox column whose declared width animates 0<->48 as
  // bulk-select toggles. Because it's never inserted/removed, MUI recomputes the pinned
  // lefts/widths on the width change and the CSS `width`/`left` transitions (see sx) let
  // the checkbox slide in and the whole pinned divider glide with it. It drives the same
  // `rowSelectionModel` the rest of the Table already consumes (bulk bar, counts, etc.).
  const rowIdOf = (row: any) => (getRowId ? getRowId(row) : row.id)
  const allRowIds = (rows as any[]).map(rowIdOf)
  const selectedSet = new Set(rowSelectionModel as (string | number)[])
  const allSelected = allRowIds.length > 0 && allRowIds.every((id: any) => selectedSet.has(id))
  const someSelected = allRowIds.some((id: any) => selectedSet.has(id)) && !allSelected
  const applySelection = (model: GridRowSelectionModel) => {
    setRowSelectionModel(model)
    if (props.onSelectionModelChange) props.onSelectionModelChange(model)
  }
  const selectColumn = {
    field: '__select__',
    headerName: '',
    // NB: MUI treats width:0 as "unset" and falls back to its 100px default, so the
    // collapsed state uses a 1px sliver (the checkbox itself is faded out via CSS).
    //
    // 56, not 48, so the checkbox column's centre lines up with the bulk-select
    // (hamburger) button directly above it in the toolbar. Measured: the toolbar
    // button is 58px wide starting at the grid's left edge minus 1, centring on
    // x=133; a 48px column centred on x=129, so the checkboxes sat 4px left of the
    // control that reveals them. 56 puts both centres on 133.
    width: showRowSelection ? 56 : 1,
    minWidth: 1,
    sortable: false,
    filterable: false,
    hideable: false,
    resizable: false,
    disableReorder: true,
    disableColumnMenu: true,
    align: 'center' as const,
    headerAlign: 'center' as const,
    renderHeader: () => (
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={(e) => applySelection(e.target.checked ? allRowIds : [])}
        tabIndex={showRowSelection ? 0 : -1}
      />
    ),
    renderCell: (params: any) => {
      const id = rowIdOf(params.row)
      return (
        <Checkbox
          checked={selectedSet.has(id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            applySelection(
              e.target.checked
                ? ([...(rowSelectionModel as any[]), id] as GridRowSelectionModel)
                : ((rowSelectionModel as any[]).filter((x) => x !== id) as GridRowSelectionModel),
            )
          }
          tabIndex={showRowSelection ? 0 : -1}
        />
      )
    },
  }
  // VRG-231 review round 2 item 3 — see the `getDetailPanelContent` prop comment
  // above for why this override exists. MUI's `hydrateColumns` pipe processor
  // merges any column WE supply for `GRID_DETAIL_PANEL_TOGGLE_FIELD` on top of
  // its own default column def (ours wins on the keys it sets), so supplying
  // only `field` + `renderCell` here overrides just the cell rendering — width/
  // align/sortable/etc. all keep DataGridPro's defaults.
  const detailPanelToggleColumnOverride = getDetailPanelContent
    ? [
        {
          field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
          renderCell: (params: any) =>
            getDetailPanelContent(params) == null ? null : <GridDetailPanelToggleCell {...params} />,
        },
      ]
    : []
  // Flex is derived at the GRID boundary only (see `withProportionalFlex`) — `parsedColumns`
  // itself stays width-authored, because the CSV export and the Columns/Sort menus consume it and
  // none of them care how wide a column renders.
  const parsedColumnsWithCheckbox = [
    ...detailPanelToggleColumnOverride,
    selectColumn,
    ...withProportionalFlex(
      parsedColumns,
      new Set(
        Object.entries(columnVisibilityModel ?? {})
          .filter(([, visible]) => visible === false)
          .map(([field]) => field),
      ),
    ),
  ]

  /**
   * CSV export. Lives in Table so all ~26 table surfaces get it from one place
   * rather than each page hand-rolling a mapper (which is how LeadsTab's export
   * ended up being the only one in the app).
   *
   * Exports what the user is LOOKING AT: current filter, current sort order, and
   * only the columns still visible.
   *
   * Declared AFTER `parsedColumns` deliberately: it closes over it and lists it as a
   * dependency, so hoisting this above that `const` is a temporal-dead-zone crash on
   * first render, not a style nit.
   *
   * VRG-227 — this used to mean "the loaded page" under server-side pagination: the grid
   * only ever holds the rows fetched for the current page, so a table reading "1 - 10 of
   * 14" exported 10 rows with no indication anything was omitted. Now, when the caller
   * wires `fetchExportPage` (see the prop doc), the toolbar's own Download click walks
   * EVERY row matching the current query in batches, so the file matches the count shown
   * in the footer rather than just what's rendered. Without `fetchExportPage` the old
   * loaded-page behavior is unchanged — deliberately: falling back silently to "less than
   * you asked for" would just move the original bug, so the button's tooltip is worded to
   * match whichever path actually runs (see the Download button below) rather than
   * overclaiming "every row" for a table that can't deliver it yet.
   *
   * The bulk bar's own "Download (N selected)" (`onlyRows`) NEVER takes the full-fetch
   * path, on purpose: a selection is already fully in memory and bounded by what the user
   * actually picked from the rows they could see — it has no "every row matching the
   * query" analogue to grow into.
   */
  const exportCsv = async (onlyRows?: any[]) => {
    const api = apiRef.current

    let visibleFields: string[] | undefined
    try {
      visibleFields = api?.getVisibleColumns?.().map((c: any) => c.field)
    } catch {
      visibleFields = undefined
    }

    if (!onlyRows && paginationMode === 'server' && fetchExportPage) {
      setIsExporting(true)
      try {
        const collected: any[] = []
        let offset = 0
        let total = Infinity
        for (;;) {
          if (collected.length >= total || collected.length >= MAX_EXPORT_ROWS) break
          const take = Math.min(EXPORT_BATCH_SIZE, MAX_EXPORT_ROWS - collected.length)
          const page = await fetchExportPage({ limit: take, offset })
          total = page.total
          if (!page.rows?.length) break
          collected.push(...page.rows)
          offset += page.rows.length
          // The server handed back fewer rows than asked for — nothing left to page
          // through, regardless of what `total` claims. Defends against a stale/wrong
          // total rather than looping forever on it.
          if (page.rows.length < take) break
        }

        // Re-sort the FULL assembled set by the table's current sort model. None of these
        // pages push sort to the server today (only filters/search are re-issued via
        // `fetchExportPage`'s closure), so the rows above arrive in the server's default
        // order; this reproduces the on-screen column-header sort over the complete result
        // instead of exporting in an order that doesn't match what the user asked to see.
        const sorted = sortRowsForExport(collected, sortModel, parsedColumns)
        const csvRows = toCsvRows(parsedColumns, sorted, visibleFields)
        if (csvRows.length) exportRowsToCsv(csvRows, exportFilename ?? 'export')

        // A cap is a disclosed decision, never a silent truncation — that silent
        // truncation is the exact bug this fix exists to remove. `total` is the server's
        // own count, so this only fires when there genuinely is more than the ceiling
        // could hold.
        if (total > MAX_EXPORT_ROWS) {
          notification({
            type: 'warning',
            title: `Exported the first ${MAX_EXPORT_ROWS.toLocaleString()} of ${total.toLocaleString()} rows`,
            text: 'Narrow your filters to get a complete file.',
          })
        }
      } catch {
        // A failed batch must never produce a partial file quietly labeled "export" — same
        // wrong-subset failure, new shape. Nothing is written; the user is told plainly.
        notification({
          type: 'error',
          title: "Couldn't prepare your download",
          // VRG-607 — "fetching your data" is developer vocabulary. The reader
          // asked for a download, so the sentence is about the download.
          text: 'Something went wrong while preparing it. Please try again.',
        })
      } finally {
        setIsExporting(false)
      }
      return
    }

    // LOADED-ROWS-ONLY PATH: client-paginated tables (where "loaded" already means "every
    // row"), the bulk bar's own selection export, and server-paginated tables that haven't
    // wired `fetchExportPage` yet.
    let data: any[] = onlyRows ?? []

    // Both paths order rows the way the grid DISPLAYS them, not the way the data
    // arrived. `selectedRowsData` is built by filtering the `rows` prop, so it carries
    // the underlying array order — sort the table by Created desc, tick three rows, and
    // the file would come out in a different order than the screen. A CSV that silently
    // reorders what you selected is the kind of wrong that is only caught by someone
    // reconciling it by hand.
    try {
      const ids = api?.getSortedRowIds?.() ?? []
      if (ids.length) {
        if (onlyRows) {
          const resolveRowId = getRowId ?? ((row: any) => row.id)
          const rank = new Map(ids.map((id: any, i: number) => [id, i]))
          data = [...onlyRows].sort(
            (a, b) => (rank.get(resolveRowId(a)) ?? 0) - (rank.get(resolveRowId(b)) ?? 0),
          )
        } else {
          data = ids.map((id: any) => api.getRow(id)).filter(Boolean)
        }
      } else if (!onlyRows) {
        data = rows
      }
    } catch {
      data = onlyRows ?? rows
    }

    const csvRows = toCsvRows(parsedColumns, data, visibleFields)
    if (!csvRows.length) return
    exportRowsToCsv(csvRows, exportFilename ?? 'export')
  }

  // Build selected rows data for bulk actions. Respect a custom `getRowId` (e.g.
  // LeadsTab keys rows by email, not `.id` — without this, selectedIds.has(row.id)
  // would never match and bulkActions would always see an empty selection).
  const selectedRowsData = useMemo(() => {
    const selectedIds = new Set(rowSelectionModel as (string | number)[])
    const resolveRowId = getRowId ?? ((row: any) => row.id)
    return (rows as any[]).filter((row: any) => selectedIds.has(resolveRowId(row)))
  }, [rowSelectionModel, rows, getRowId])

  // Ask parent for available actions given the current selection, then ALWAYS offer
  // "Download CSV" for that selection. Appended here rather than added to each page's
  // bulkActions so every table gets selection-scoped export from one place -- and so a
  // table that passes no bulkActions at all still gets it.
  const actions = [
    ...(bulkActions ? bulkActions(selectedRowsData).filter((a: any) => a.visible !== false) : []),
    ...(hideExport ? [] : [{
      // "Download", not "Download CSV" — matches the toolbar button, and the file
      // format is an implementation detail rather than part of the action's name.
      label: `Download${selectedRowsData.length ? ` (${selectedRowsData.length})` : ''}`,
      icon: <DownloadIcon strokeWidth={1} />,
      onClick: () => { void exportCsv(selectedRowsData) },
    }]),
  ]

  // Centers the empty-state block in the grid's rows viewport. MUI's `noRowsOverlay`
  // slot renders inside `.MuiDataGrid-overlayWrapperInner`, which DataGrid sizes to
  // the full rows-viewport height but does NOT itself center its content (it's a
  // plain block box) — without this wrapper the empty state sits flush at the top.
  // Applies to every table regardless of height mode (fillBottom / topOffset /
  // fixed `height`): all three ultimately give the grid a real height, and this
  // wrapper always fills 100% of whatever that resolves to.
  function CustomEmptyState() {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        {emptyState}
      </Box>
    )
  }

  // console.debug('ACTIONS', actions)

  // `fillBottom` (full-page tables only): size the table so its bottom sits the
  // SAME 24px gap from the viewport bottom as the page's L/R gutter (Layout `main`
  // p:6 = 24px). Measured rather than guessed, so it is robust to differing header
  // heights — unlike the per-page `topOffset` magic number, which stays the
  // first-paint fallback. NOT for pages where the table shares space with content
  // below it (e.g. a panel under the grid) — those keep the fixed `topOffset`.
  //
  // The measurement itself now lives in `useFillBottom` (§28/§36): the certificate
  // Scans tab needed the identical behaviour and had hand-rolled a thinner copy that
  // went stale on every reflow that was not a window resize. One implementation, two
  // consumers. `measureSelector` is why the hook takes one at all — this box also
  // wraps the ActionLine above the grid, so measuring the BOX top would overflow the
  // viewport by that row's height; the GRID's top is the real fill start.
  //
  // Default: fill on full-page tables (no explicit `topOffset`), skip on
  // embedded/tabbed tables (which set a `topOffset` to fit their chrome and may
  // have content below the grid). Callers can still force it either way.
  const shouldFillBottom = (fillBottom ?? !topOffset) && heightOverride == null && !fillParent
  const { ref: outerBoxRef, height: fillHeight } = useFillBottom<HTMLDivElement>({
    enabled: shouldFillBottom,
    measureSelector: '.MuiDataGrid-root',
    deps: [rowSelectionModel.length, loading, rows?.length],
  })

  // When the grid has no rows, the empty-state overlay fills the body — but the
  // pinned-column fillers still draw their vertical dividers down the empty area,
  // which reads as a stray line floating beside the centred empty state. Drop
  // those divider borders while empty so the empty state sits in a clean box.
  const isEmptyTable = !loading && (!rows || rows.length === 0)
  // Separate from `isEmptyTable`, which gates the empty-STATE message and so must not
  // fire mid-load. The pinned-column dividers care about something simpler: whether
  // there are rows for them to divide. There are none while loading either, so the
  // divider was running down beside a blank grid on every first paint — a stray
  // vertical line with nothing on either side of it.
  const hasNoRows = !rows || rows.length === 0

  // VRG-227 — the Download tooltip must describe what THIS click actually does, which
  // differs by pagination mode (and, under server pagination, by whether the caller wired
  // `fetchExportPage`). A tooltip that claimed "every row" on a table that can only ever
  // hand back the loaded page would just move the silent-subset bug into the copy.
  const downloadTooltip = paginationMode === 'server'
    ? (fetchExportPage
        ? 'Download every row matching your filters as a CSV file — not just this page.'
        : 'Download the rows on this page as a CSV file.')
    : 'Download every row in this table as a CSV file.'

  return (
    <>

        <Box ref={outerBoxRef} data-tour={dataTour} sx={{
            height: fillParent
              ? '100%'
              : heightOverride != null
              ? heightOverride
              : (shouldFillBottom && fillHeight != null)
              ? `${fillHeight}px`
              : vhMinus((topOffset ? topOffset : 176) + (rowSelectionModel.length > 0 ? 42 : 0)),
            // fillParent: flex column so the grid (flex:1) fills the remaining
            // height after the ActionLine, bottom = parent bottom, no overflow.
            ...(fillParent && { display: 'flex', flexDirection: 'column', minHeight: 0 }),
            // NO height transition: `fillHeight` is measured in useLayoutEffect
            // (pre-paint), so the table renders at its final height on first paint.
            // A transition here animated the initial fallback-calc → measured-height
            // jump, making every table "grow in" from a short height on every page
            // load. Zoom/bulk-bar height changes snap instantly instead — correct.
          }}>
          {/* Top margin on the TOOLBAR, not bottom margin on each alert. Anything a page renders
              above its table — an error banner, a warning, a callout — otherwise butts straight
              into the search/filter row with no separation (see the Orders page's "Error loading
              orders"). Putting the gap on the toolbar means every such element gets it for free,
              from one place, instead of each page remembering to add its own. It also gives the
              toolbar a little air under the page title when there is no alert at all.

              That gap is now `mt: 4` INSIDE `ActionLine` rather than `sx` here (VERA-455) — same
              reasoning taken one step further: a toolbar rendered without a Table (Tags → Shop,
              Support, Scans, Activity) was getting the bottom gap and not the top one. */}
          {showActionLine && (
            <ActionLine
              loading={loading}
              apiRef={apiRef} 
              showSearch={showSearch}
              showBulkActions={Boolean(bulkActions) && !alwaysShowRowSelection} // Hide bulk action toggle when alwaysShowRowSelection is true
              showRowSelection={showRowSelection}
              bulkActionsToggle={() => {
                apiRef.current.setRowSelectionModel([])
                setShowRowSelection(!showRowSelection)
              }}
              search={search}
              onSearchChange={onSearchChange}
              showScan={showScan}
              scanEntity={scanEntity}
              serverSide={Boolean(onSearchChange)}
              afterSearchComponents={actionLineLeftComponents}
              rightComponents={
                hideExport ? actionLineRightComponents : (
                  <>
                    {actionLineRightComponents}
                    {/* Table toolbar, beside Filter/Sort — the table's own top bar, so
                        every surface gets export in the same spot instead of 26 page
                        headers each deciding where to put it. Exports every row matching
                        the current filter + sort when `fetchExportPage` is wired (VRG-227),
                        otherwise the loaded page (see `downloadTooltip` + `exportCsv`'s own
                        doc); the bulk bar handles selection-scoped export separately.

                        LABELLED, like its Views/Filter/Sort/Layout peers. It was briefly
                        icon-only, on the argument that a download glyph is universally read
                        and this is a one-off action rather than a list-shaping control.
                        That argument is fine in isolation and wrong in context: it made
                        this the ONE unlabelled control in a row of five, and a mixed row is
                        worse than either consistent choice. All-icons is not available here
                        because two of the peers cannot carry an icon — "Views" surfaces
                        USER-NAMED saved configurations (an icon cannot say "Unsold EU
                        stock"), and "Layout" names a concept the product is still teaching
                        rather than one the reader already holds.

                        ⚠️ THAT ARGUMENT IS ABOUT THE RELAXED ROW, and it still holds there.
                        It does NOT survive into a row that cannot fit five labels: the
                        choice under pressure is not "mixed vs consistent", it is "mixed vs
                        TWO ROWS". So this label is the FIRST to go as the container narrows
                        (VRG-535) — the argument above is exactly why it is first rather than
                        why it never goes. The glyph being universally read and the action
                        being one-off are what make it the cheapest of the five to unlabel;
                        "Layout" is last for the mirror-image reason. See the ladder in
                        `toolbarCollapse.ts`.

                        The tooltip stays regardless of the label, because the label cannot
                        answer the question that matters: whether this click gets every
                        matching row or only what is on screen. `downloadTooltip` is
                        computed per pagination mode so it can never overclaim. That it was
                        already unconditional is why this button needed no new affordance to
                        become collapsible — the tooltip it drops back to says MORE than the
                        word it loses. */}
                    <Tooltip title={downloadTooltip} enterDelay={600}>
                      <Stack>
                        <Button
                          size="large"
                          variant="secondary"
                          disableElevation
                          onClick={() => { void exportCsv() }}
                          disabled={loading || !rows?.length}
                          loading={isExporting}
                          // The accessible name, stated rather than left to the label text.
                          // CSS cannot conditionally supply one, and the collapsed button's
                          // visible text is `display: none` — which removes it from the
                          // accessibility tree as well as the layout. Without this, the
                          // narrow-container button announces as an unnamed button.
                          // Unconditional on purpose: at full width it is byte-identical to
                          // the text it overrides, so there is no state where the two
                          // disagree and no width where a screen reader hears less.
                          aria-label="Download"
                          // `startIcon`, not an icon child beside the text — that is how
                          // every peer in this row (Views, Filter, Sort, Layout) renders,
                          // and it is what supplies the icon-to-label gap. Placing the icon
                          // as a sibling child gives no gap at all, which is what a first
                          // pass at re-labelling this button produced.
                          startIcon={!isExporting ? <DownloadIcon size={18} strokeWidth={1.5} /> : undefined}
                          // Zeroes `startIcon`'s 8px/-4px margins while the label is hidden,
                          // so the collapsed button centres its glyph instead of sitting it
                          // 12px right of centre. See `toolbarStartIconSx`.
                          sx={toolbarStartIconSx('download')}
                        >
                          <Box component="span" sx={toolbarLabelSx('download')}>Download</Box>
                        </Button>
                      </Stack>
                    </Tooltip>
                  </>
                )
              }
              showFilters={showFilters}
              filters={filters}
              extraFilters={extraFilters}
              onFiltersApply={onFiltersApply}
              extraFiltersDirty={extraFiltersDirty}
              onClearExtraFilters={onClearExtraFilters}
              showSort={showSort} 
              sort={sortModel}
              sortIsDefault={isDefaultSort}
              onSortChange={undefined}
              onFiltersChange={onFiltersChange}
              showLayoutOptions={showLayoutOptions}
              layout={layoutModel}
              density={densityModel}
              columns={columns}
              visibilityModel={columnVisibilityModel}
              onResetColumns={resetColumns}
              rows={rows}
              beforeFiltersComponents={viewKey ? (
                <ViewsDropdown viewKey={viewKey} currentState={currentViewState} applyState={applyViewState} onReset={resetAll} />
              ) : undefined}
            />
          )}
          {/* TODO: move to a BulkActionComponent as part of ActionLine? and consider select all rows https://mui.com/x/react-data-grid/row-selection/?srsltid=AfmBOopaw3DgC2Z80XlASRxo2LfZuiBFsKBxq4rSjNbdQSPKH_SsGQtw#visible-rows-selection */}
          {/* TODO: apiRef.getSelectedRows() when we need to perform the bulk action */}
          <Collapse in={rowSelectionModel.length > 0}>
            <Stack
            sx={{
              minWidth: '100%',
              height: 50,
              marginBottom: -2,
              // background: `linear-gradient(90deg, rgba(130, 90, 195, 1) 0%, rgb(55, 39, 137) 100%)`,
              // A raised SURFACE, matching the grid it sits on top of — `background.paper` and
              // the same grey[100] edge `Card` uses. It was grey[50] on a primary[100] border,
              // which in light put it within ~1.01:1 of the #f4f5f5 page (so the bar read as part
              // of the background rather than as a control strip) and drew a noticeably darker
              // edge, since primary[100] is the DARK-mode border token.
              backgroundColor: theme.palette.background.paper,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              border: `1px solid ${theme.palette.grey[100]}`,
              px: 3,
            }}>
              <Stack sx={{height: 42}} direction="row" spacing={3} alignItems="center">
                <Stack direction="row" spacing={1} pl={2}>
                  <Typography variant="body3">{rowSelectionModel.length}</Typography>
                  <Typography variant="body3" color="text.secondary">{rowSelectionModel.length > 1 ? ' rows' : ' row'} selected</Typography>
                </Stack>
                {actions.length == 0 && (
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* <TriangleAlertIcon strokeWidth={1} size={20}/> */}
                      <Typography variant="body3" color="text.secondary">You do not have permissions to perform any actions on the selected rows</Typography>
                    </Stack>
                  </Stack>
                )}
                {actions.length > 0 && (
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Divider orientation="vertical" flexItem />
                    {actions.map((action: any, index: number) => {
                      const button = (
                        <Button
                          variant="text"
                          key={`action-${index}`}
                          startIcon={action.icon}
                          onClick={action.onClick}
                          disabled={action.disabled}
                          destructive={action.destructive}
                          sx={{fontSize: '12px !important', px: 3, py: 2}}
                        >
                          <Typography variant='body3'>{action.label}</Typography>
                          {/* {!['Delete','Edit']?.includes(action.label) && rowSelectionModel.length > 1 && 's'} */}
                        </Button>
                      )
                      // §27: a bulk action the user lacks the permission for stays VISIBLE and goes
                      // disabled with a tooltip naming the permission, same as its row-kebab twin.
                      // Without `disabled`/`tooltip` here every page had to gate bulk actions by
                      // `visible:` instead, which is why the kebab and the bulk bar disagreed on the
                      // same capability (PackagesPage "Add tags" was disabled in one and gone in the
                      // other). A disabled MUI Button swallows pointer events, so the tooltip needs a
                      // wrapper element to have a hover target.
                      return action.tooltip ? (
                        <Tooltip key={`action-${index}`} title={action.tooltip} arrow placement="top">
                          {action.disabled ? <span style={{display: 'inline-flex'}}>{button}</span> : button}
                        </Tooltip>
                      ) : (
                        button
                      )
                    })}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Collapse>
           <StyledDataGrid
            ref={tableRef}
            apiRef={apiRef}
            initialState={{
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
              sorting: {
                sortModel: sortModel,
              },
              filter: {
                filterModel: filterModel,
              },
              density: densityModel,
              // VRG-703 — an uncontrolled starting page/size (see `resolvePagination`).
              ...(pagination.initial ? { pagination: { paginationModel: pagination.initial } } : {}),
            }}
            pinnedColumns={{
              // VRG-236: when a caller opts into the detail-panel toggle, keep it
              // pinned alongside the checkbox column (MUI otherwise renders it as
              // the leftmost REGULAR column, i.e. immediately right of the pinned
              // block but not pinned itself, which would let it scroll away
              // horizontally). Absent `getDetailPanelContent`, this array is
              // identical to before.
              left: [
                ...(getDetailPanelContent ? [GRID_DETAIL_PANEL_TOGGLE_FIELD] : []),
                '__select__',
                // VRG-231 — the auto-inserted tree grouping column takes the same
                // leftmost slot a caller's own pinned-left data column used to
                // occupy (see the `groupingColDef` prop doc), so it's pinned here
                // rather than via `leftPinnedColumns` (which only reads the
                // caller's own `columns`, and this field never appears there).
                ...(treeData && hasMuiXLicense ? [GRID_TREE_DATA_GROUPING_FIELD] : []),
                ...leftPinnedColumns,
              ],
              right: rightPinnedColumns
            }}
            sx={{
              zIndex: 1,
              // fillParent: take the remaining flex-column height (after the
              // ActionLine) so the grid bottom = the outer box bottom, no overflow.
              ...(fillParent && { flex: 1, minHeight: 0 }),
              // VERA-914 — SMOOTH bulk-select slide. The checkbox column is always
              // mounted (checkboxSelection stays true) and its width animates 0<->48
              // (see `checkboxColumn`). These transitions make the change glide: the
              // checkbox cell/header widen from 0, the pinned data columns' `left`
              // shifts right, and the pinned-left filler (below-rows divider) widens —
              // all together, so the whole pinned divider slides as one.
              '& [data-field="__select__"]': {
                transition: animateLayout
                  ? 'width 300ms ease-in-out, min-width 300ms ease-in-out, max-width 300ms ease-in-out'
                  : 'none',
                overflow: 'hidden',
                padding: 0,
                justifyContent: 'center',
                // When collapsed the (1px) checkbox must not be clickable.
                ...(!showRowSelection && { pointerEvents: 'none' }),
              },
              // The header cell's title container carries an 8px left padding that
              // pushed the header checkbox ~5px right of the row checkboxes — zero it
              // and center so the header + row checkboxes line up.
              '& [data-field="__select__"] .MuiDataGrid-columnHeaderTitleContainer': {
                padding: 0,
                justifyContent: 'center',
              },
              // Fade the checkbox itself so the 1px collapsed sliver is fully invisible,
              // then fade in as the column slides open.
              '& [data-field="__select__"] .MuiCheckbox-root': {
                transition: 'opacity 300ms ease-in-out',
                opacity: showRowSelection ? 1 : 0,
              },
              '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedLeft': {
                transition: animateLayout ? 'left 300ms ease-in-out, width 300ms ease-in-out' : 'none',
              },
              '& .MuiDataGrid-filler--pinnedLeft': {
                transition: animateLayout ? 'width 300ms ease-in-out' : 'none',
              },
              // No rows — empty OR still loading: suppress the pinned-column filler
              // dividers so no stray vertical line runs down beside a blank grid.
              ...(hasNoRows && {
                '& .MuiDataGrid-filler--pinnedRight': { borderLeft: 'none' },
                '& .MuiDataGrid-filler--pinnedLeft': { borderRight: 'none' },
              }),
              ...sx
            }}
            loading={loading}
            columnHeaderHeight={44}
            rowHeight={72}
            disableRowSelectionOnClick={true}
            rows={rows as any}
            pinnedRows={pinnedRows as any}
            columns={parsedColumnsWithCheckbox as any}
            getRowId={getRowId}
            checkboxSelection={false}
            rowSelectionModel={rowSelectionModel}
            keepNonExistentRowsSelected={true}
            onRowSelectionModelChange={(model) => {
              setRowSelectionModel(model)
              if (props.onSelectionModelChange) {
                props.onSelectionModelChange(model)
              }
            }}
            // rowSelectionModel={rowSelectionModel}
            getRowClassName={getRowClassName}
            ignoreDiacritics
            disableColumnMenu 
            disableColumnFilter
            // autosizeOnMount={true} 
            headerFilters={false}
            filterModel={filterModel}
            onFilterModelChange={(model) => {
              setFilterModel(model)
              if (externalOnFilterModelChange) externalOnFilterModelChange(model)
            }}
            sortModel={sortModel}
            sortingMode={sortingMode}
            onSortModelChange={(model) => {
              setSortModel(model)
              if (externalOnSortModelChange) externalOnSortModelChange(model)
            }}
            disableMultipleColumnsSorting={false}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
            density={densityModel}
            onDensityChange={(model) => {
              if (onDensityChange) onDensityChange(model)
              setDensityModel(model)
            }}
            pagination
            filterMode={filterMode}
            paginationMode={paginationMode}
            rowCount={rowCount}
            // Must include every value the custom RowsPerPageSelector can set (25/50/100/All=-1);
            // MUI discards a controlled pageSize that isn't a preset option, which silently reset
            // the page-size to a default (VERA-790: 25 was being clamped to 10).
            pageSizeOptions={[25, 50, 100, -1]}
            // VRG-703 — controlled only when the caller also handles the change; otherwise
            // the model went into `initialState` above. A sort or filter change returns to
            // page 1 (MUI calls setPage(0), which reaches a controlled caller's handler too).
            {...(pagination.controlled !== undefined ? { paginationModel: pagination.controlled } : {})}
            onPaginationModelChange={onPaginationModelChange}
            resetPageOnSortFilter
            // unstable_listColumn={listColDef}
            // unstable_listView={layoutModel == 'grid'? true : false}
            // VRG-236: the ONLY place these two props are referenced. Narrowly
            // conditioned on `getDetailPanelContent` (not a general {...rest}
            // spread) — when a caller doesn't pass it, `<StyledDataGrid>` gets
            // neither prop at all, so its rendered config is unchanged.
            {...(getDetailPanelContent
              ? {
                  getDetailPanelContent,
                  getDetailPanelHeight: getDetailPanelHeight ?? (() => 'auto' as const),
                }
              : {})}
            // VRG-231: the ONLY place these are referenced. Narrowly conditioned on
            // `treeData && hasMuiXLicense` (not a general {...rest} spread) — a
            // table that doesn't opt in gets none of these props, so its rendered
            // config is unchanged. Community fallback: `rows` is already trimmed
            // to top-level-only above, and none of these three props reach the
            // grid at all.
            {...(treeData && hasMuiXLicense
              ? {
                  treeData: true,
                  getTreeDataPath,
                  groupingColDef,
                }
              : {})}
            hideFooter={!showFooter}
            slots={{
              pagination: CustomFooter,
              columnHeaders: showColumnHeaders ? undefined : HiddenColumnHeaders,
              noRowsOverlay: CustomEmptyState,
              loadingOverlay: CustomLoader,
              ...(treeData && hasMuiXLicense
                ? { treeDataCollapseIcon: TreeDataCollapseIcon, treeDataExpandIcon: TreeDataExpandIcon }
                : {}),
            }}
            slotProps={{
              // loadingOverlay: {
              //   variant: 'circular-progress',
              //   noRowsVariant: 'circular-progress',
              // }
            }}
          />
        </Box>
    </>
  );
}
